import $ from 'jquery';
import config from './config';
import * as Utils from './utils';

const W = window;
const INIT_TIMEOUT = 1000;
const REQUEST_TIMEOUT = 5000;
const genID = () => Math.random().toString(36).substring(2);

class ClientWorker {
  constructor() {
    this.id = genID();
    this.waitFor = {};
    this.body = $('body');
    this.proxy = $({});

    $.onAuth(this.onInit.bind(this));
    W.addEventListener('online', this.onNetwork.bind(this));
    W.addEventListener('offline', this.onNetwork.bind(this));
  }

  on(name, handler) {
    if (name === 'init' && this.init) return handler();
    this.proxy.on(name, handler);
    return this;
  }

  off(name, handler) {
    this.proxy.off(name, handler);
    return this;
  }

  async waitForInit() {
    if (this.init) return;

    const start = new Date().getTime();

    await new Promise((resolve, reject) => {
      const checkForInit = () => {
        const now = new Date().getTime();

        if (this.init) return resolve();
        if (now - start > INIT_TIMEOUT) return reject('Timed out waiting for worker.');

        setTimeout(checkForInit, 250);
      };

      checkForInit();
    });
  }

  async onInit(auth) {
    const { pathname } = location;

    if (!auth.authenticated || !$.hasPerms('photos')) return;

    this.worker = new SharedWorker(Utils.baseUri('shared-worker.js'), { name: 'oas3' });
    this.worker.port.addEventListener('message', this.onMessage.bind(this));
    this.worker.port.start();

    this.auth = {
      website: Utils.pick(auth.website, ['id', 'name']),
      user: Utils.pick(auth.user, ['id', 'first_name', 'last_name']),
    };

    navigator.locks.request(this.id, async () => {
      const result = await this.sendRequest('init', {
        id: this.id,
        type: pathname === Utils.baseUri('photos') ? 'manager' : 'client',
      });

      if (result) {
        console.groupCollapsed('🧑‍💼 WORKER:CONNECTED');
        console.log('ID', this.id);
        console.groupEnd();

        //W.addEventListener('beforeunload', this.onClose.bind(this));

        this.body.addClass('worker-init');
        this.body[result.ready ? 'addClass' : 'removeClass']('worker-ready');
        this.proxy.triggerHandler('init');
        this.init = true;
      }

      return new Promise(() => {});
    });
  }

  async onClose() {
    await this.sendRequest('close', {
      id: this.id,
    });

    console.log('🧑‍💼 WORKER:DISCONNECTED');
  }

  async onPromote(src) {
    this.file_worker = new Worker(Utils.baseUri('file-worker.js'));
    this.file_worker.addEventListener('message', this.onMessage.bind(this));

    console.log('🧑‍💼 WORKER:PROMOTED');

    this.manager = true;
    this.proxy.triggerHandler('promoted');
    return this.sendResponse(src, true);
  }

  async onNetwork(evt) {
    if (this.manager) {
      await this.sendMessage('state', {
        network: evt.type,
        queue: this.db.state,
      });
    }
  }

  async onMessage(evt) {
    const { id, payload } = evt.data;
    const [action, result] = evt.data.action.split(':');

    //console.log('onMessage', { id, action, result });

    /* prettier-ignore */
    if (result) {
      const waitFor = this.waitFor[`${action}:${id}`];

      if (waitFor) {
        clearTimeout(waitFor.timeout);

        if (result === 'ack') waitFor.resolve(payload);
        else waitFor.reject(payload);
      }
    } 
    else {
      if (id) {
        const src = { id, action };

        /* prettier-ignore */
        try {
          switch(action) {
            case 'promote': await this.onPromote(src); break;  
            case 'get-stats': await this.getStats(src); break;
            case 'get-state': await this.getState(src); break;
            case 'set-state': await this.setState(payload, src); break;
            case 'list-photos': await this.listPhotos(payload, src); break;
            case 'count-photos': await this.countPhotos(payload, src); break;
            case 'remove-photos': await this.removePhotos(payload, src); break;
            case 'add-photo': await this.addPhoto(payload, src); break;
            default:
              console.log(`Unhandled action "${action}"`);
          }
        }
        catch(err) {
          await this.sendError(src, err);
        }
      }
      else {
        switch(action) {
          case 'ready': 
          case 'not-ready':
            this.body[action === 'ready' ? 'addClass' : 'removeClass']('worker-ready');
            break;

          case 'file-error':
          case 'file-added':
          case 'file-progress':
          case 'file-completed':
          case 'file-failure':
          case 'files-removed':
            await this.sendMessage(action.replace('file', 'photo'), payload);
            break;

          default:
            this.proxy.triggerHandler(action, [payload]);
        }
      }
    }
  }

  async sendMessage(action, payload) {
    //console.log('sendMessage', { action });

    this.worker.port.postMessage({
      action,
      payload,
    });
  }

  async sendRequest(action, payload) {
    const id = genID();
    const name = `${action}:${id}`;
    const events = ['get-stats', 'get-queue', 'set-queue', 'list-files', 'count-files', 'save-file', 'load-file', 'delete-files'];
    let worker = this.worker.port;

    if (events.includes(action)) {
      if (this.manager) worker = this.file_worker;
      else if (action !== 'get-stats') throw new Error('Invalid request "${action}"');
    }

    //console.log('sendRequest', { id, action });

    /* prettier-ignore */
    const result = await new Promise((resolve, reject) => {
      const timeout = setTimeout(
        () => {
          if (this.waitFor[name]) reject(`Timed out waiting for "${name}" response`);
        }, 
        REQUEST_TIMEOUT
      );

      this.waitFor[name] = { resolve, reject, timeout };

      worker.postMessage({
        id,
        action,
        payload,
      });
    });

    delete this.waitFor[name];

    return result;
  }

  async sendError(dest, payload) {
    const { id, action } = dest;

    //console.log('sendResponse', { id, action });

    this.worker.port.postMessage({
      id,
      action: `${action}:nak`,
      payload,
    });
  }

  async sendResponse(dest, payload) {
    const { id, action } = dest;

    //console.log('sendResponse', { id, action });

    this.worker.port.postMessage({
      id,
      action: `${action}:ack`,
      payload,
    });
  }

  async getStats(src) {
    await this.waitForInit();

    if (this.manager) {
      const stats = await this.sendRequest('get-stats');
      if (src) await this.sendResponse(src, stats);
      return stats;
    }

    return this.sendRequest('get-stats');
  }

  async getState(src) {
    await this.waitForInit();

    if (this.manager) {
      const queue = await this.sendRequest('get-queue');
      const state = {
        network: navigator.onLine ? 'online' : 'offline',
        queue,
      };

      if (src) await this.sendResponse(src, state);
      return state;
    }

    return this.sendRequest('get-state');
  }

  async setState(value, src) {
    await this.waitForInit();

    if (this.manager) {
      await this.sendRequest('set-queue', value);

      if (src) await this.sendResponse(src);

      await this.sendMessage('state-changed', {
        network: navigator.onLine ? 'online' : 'offline',
        queue: value,
      });

      return;
    }

    return this.sendRequest('set-state', value);
  }

  async listPhotos(filters, src) {
    await this.waitForInit();

    if (this.manager) {
      const rows = (await this.sendRequest('list-files', filters)).map(row => {
        const { api, auth, ...data } = row;
        return data;
      });

      if (src) await this.sendResponse(src, rows);
      return rows;
    }

    return this.sendRequest('list-photos', filters);
  }

  async countPhotos(filters, src) {
    await this.waitForInit();

    if (this.manager) {
      const count = await this.sendRequest('count-files', filters);

      if (src) await this.sendResponse(src, count);
      return count;
    }

    return this.sendRequest('count-photos', filters);
  }

  async addPhoto(file, src) {
    await this.waitForInit();

    if (this.manager) {
      await this.sendRequest('save-file', file);

      if (src) await this.sendResponse(src, true);
      //await this.sendMessage('photo-added', Utils.omit(file, ['api', 'auth']));
      return true;
    }

    return this.sendRequest('add-photo', {
      api: W.API,
      auth: this.auth,
      upload: config.upload,
      ...file,
    });
  }

  async removePhotos(filters, src) {
    await this.waitForInit();

    if (this.manager) {
      await this.sendRequest('delete-files', filters);

      if (src) await this.sendResponse(src, true);
      //await this.sendMessage('photos-removed', deleted);
      return true;
    }

    return this.sendRequest('remove-photos', filters);
  }
}

navigator.serviceWorker.getRegistrations().then(regs => {
  regs.forEach(reg => {
    reg.unregister();
  });
});

$.Worker = new ClientWorker();
